import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  CalendarDaysIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { format } from 'date-fns';
import type { Event } from '@flock-types/events.types';
import type { Role, UserOrganization } from '@flock-types/auth.types';
import { useAuth } from '@/context/AuthContext';
import { hasRoleAtLeast } from '@/components/layout/RoleGate';
import { useToast } from '@/components/Toast';
import { eventsService } from '@/services/events.service';
import { getGroupColor } from './calendar/groupColors';
import { dayKey } from './calendar/useCalendarEvents';
import EventChip from './calendar/EventChip';
import EventLocationRow from './EventLocationRow';
import type { CalendarEventInstance } from './calendar/types';
import RsvpSummary from './rsvp/RsvpSummary';
import RsvpList from './rsvp/RsvpList';
import AttachmentList from './attachments/AttachmentList';
import AttachmentUploader from './attachments/AttachmentUploader';

/** Fields the API returns but the shared Event type doesn't declare. */
type EventDetail = Event & { enable_rsvp?: boolean; status?: string };

const ROLE_HIERARCHY: Role['role'][] = [
  'org_admin',
  'campus_pastor',
  'ministry_director',
  'group_leader',
  'group_member',
];

/** Highest role the user holds in a specific org (cross-org calendar). */
function roleInOrg(orgs: UserOrganization[], orgId: string): Role['role'] | null {
  const membership = orgs.find((o) => o.organization_id === orgId);
  if (!membership) return null;
  for (const role of ROLE_HIERARCHY) {
    if (membership.roles.some((r) => r.role === role)) return role;
  }
  return null;
}

const RSVP_OPTIONS = [
  { value: 'yes', label: 'Going', activeClass: 'bg-green-600 text-white border-green-600' },
  { value: 'maybe', label: 'Maybe', activeClass: 'bg-amber-500 text-white border-amber-500' },
  { value: 'no', label: "Can't go", activeClass: 'bg-flock-navy-600 text-white border-flock-navy-600' },
] as const;

export type SlideOverState =
  | {
      kind: 'event';
      eventId: string;
      /** Org the event belongs to — requests are pinned to it (cross-org calendar). */
      organizationId: string;
      /** The clicked occurrence (differs from master start for virtual instances). */
      occurrenceStart?: Date;
      isVirtual?: boolean;
      groupId?: string;
      groupName?: string;
    }
  | { kind: 'day'; date: Date };

interface Props {
  state: SlideOverState;
  eventsByDay: Map<string, CalendarEventInstance[]>;
  onClose: () => void;
  onEdit: (event: Event, organizationId?: string) => void;
  showGroup: boolean;
}

function EventBody({
  eventId,
  organizationId,
  occurrenceStart,
  isVirtual,
  groupId,
  groupName,
  onClose,
  onEdit,
}: {
  eventId: string;
  organizationId: string;
  occurrenceStart?: Date;
  isVirtual?: boolean;
  groupId?: string;
  groupName?: string;
  onClose: () => void;
  onEdit: (event: Event, organizationId?: string) => void;
}) {
  const { currentOrganization, organizations } = useAuth();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const orgId = organizationId;
  const isCrossOrg = orgId !== currentOrganization?.id;
  // Leader actions follow the role in the EVENT'S org, not the current one
  const isLeader = hasRoleAtLeast(roleInOrg(organizations, orgId), 'group_leader');

  const eventQuery = useQuery({
    queryKey: [orgId, 'event', eventId],
    queryFn: () => eventsService.getEvent(eventId, orgId),
  });

  const rsvpsQuery = useQuery({
    queryKey: [orgId, 'event-rsvps', eventId],
    queryFn: () => eventsService.getEventRSVPs(eventId, orgId),
  });

  const attachmentsQuery = useQuery({
    queryKey: [orgId, 'event-attachments', eventId],
    queryFn: () => eventsService.getAttachments(eventId, orgId),
  });

  const rsvpMutation = useMutation({
    mutationFn: (status: 'yes' | 'maybe' | 'no') =>
      eventsService.submitRSVP(eventId, status, orgId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [orgId, 'event', eventId] });
      queryClient.invalidateQueries({ queryKey: [orgId, 'event-rsvps', eventId] });
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
    },
    onError: () => showToast("Couldn't save your RSVP. Try again.", 'error'),
  });

  const event = eventQuery.data as EventDetail | undefined;
  const rsvpData = rsvpsQuery.data;
  // Events are org-visible but RSVPs are member-only — a 403 here just means
  // the viewer isn't in this event's group.
  const rsvpsForbidden =
    (rsvpsQuery.error as { response?: { status?: number } } | null)?.response?.status === 403;

  if (eventQuery.isLoading) {
    return (
      <div className="space-y-3 p-5">
        <div className="h-6 w-2/3 animate-pulse rounded bg-flock-navy-50" />
        <div className="h-4 w-1/2 animate-pulse rounded bg-flock-navy-50" />
        <div className="h-24 animate-pulse rounded-xl bg-flock-navy-50" />
      </div>
    );
  }

  if (!event) {
    return <div className="p-5 text-sm text-flock-navy-400">Event not found.</div>;
  }

  const start = occurrenceStart ?? new Date(event.start_time);
  const masterEnd = event.end_time ? new Date(event.end_time) : null;
  const masterStart = new Date(event.start_time);
  const end = masterEnd
    ? new Date(start.getTime() + (masterEnd.getTime() - masterStart.getTime()))
    : null;
  const isPast = start.getTime() < Date.now();
  const rsvpEnabled = event.enable_rsvp !== false;
  const cancelled = event.status === 'cancelled';
  const color = getGroupColor(groupId ?? event.group_id);

  const handleDelete = async () => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await eventsService.deleteEvent(event.id, orgId);
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      showToast('Event deleted');
      onClose();
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      showToast(
        status === 403
          ? 'You can only manage events for groups you lead.'
          : "Couldn't delete the event.",
        'error'
      );
    }
  };

  return (
    <>
      <div className="flex items-start gap-2 px-5 pt-1">
        <div className="min-w-0 flex-1">
          {groupName && (
            <span
              className={clsx(
                'mb-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-medium',
                color.chipBg,
                color.chipText
              )}
            >
              {groupName}
            </span>
          )}
          <h2 className="text-lg font-semibold leading-snug text-flock-navy-900">
            {event.title}
            {cancelled && (
              <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 align-middle text-[10px] font-semibold uppercase text-red-700">
                Cancelled
              </span>
            )}
          </h2>
        </div>
        {isLeader && (
          <>
            <button
              onClick={() => onEdit(event, orgId)}
              title="Edit event"
              className="rounded-lg p-2 text-flock-navy-400 transition hover:bg-flock-navy-50"
            >
              <PencilSquareIcon className="h-5 w-5" />
            </button>
            <button
              onClick={handleDelete}
              title="Delete event"
              className="rounded-lg p-2 text-flock-navy-400 transition hover:bg-red-50 hover:text-red-600"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </>
        )}
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto px-5 pb-5 pt-3">
        <div className="rounded-xl bg-flock-navy-50/60 p-3 text-sm text-flock-navy-700">
          <p className="flex items-center gap-2">
            <CalendarDaysIcon className="h-4 w-4 shrink-0 text-flock-navy-400" />
            {format(start, 'EEEE, MMMM d')}
            {!event.recurrence || event.recurrence === 'none' ? '' : null}
          </p>
          <p className="ml-6 text-flock-navy-500">
            {format(start, 'h:mm a')}
            {end ? ` – ${format(end, 'h:mm a')}` : ''}
          </p>
          {event.location && (
            <EventLocationRow event={event} className="mt-1 text-flock-navy-700" />
          )}
          {event.recurrence && event.recurrence !== 'none' && (
            <p className="mt-1 flex items-center gap-2 text-xs text-flock-navy-400">
              <ArrowPathIcon className="h-3.5 w-3.5 shrink-0" />
              Repeats {event.recurrence}
              {isVirtual ? ' · responses apply to the whole series' : ''}
            </p>
          )}
        </div>

        {event.description && (
          <p className="line-clamp-6 whitespace-pre-wrap text-sm text-flock-navy-800">
            {event.description}
          </p>
        )}

        {((attachmentsQuery.data?.length ?? 0) > 0 || (isLeader && !isPast)) && (
          <div>
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-flock-navy-400">
              Attachments
            </div>
            <AttachmentList
              eventId={event.id}
              organizationId={orgId}
              attachments={attachmentsQuery.data ?? []}
            />
            {!cancelled && isLeader && (
              <div className={clsx((attachmentsQuery.data?.length ?? 0) > 0 && 'mt-2')}>
                <AttachmentUploader eventId={event.id} organizationId={orgId} />
              </div>
            )}
          </div>
        )}

        {rsvpsForbidden && (
          <p className="rounded-xl bg-flock-navy-50/60 p-3 text-xs text-flock-navy-400">
            RSVPs are visible to members of {groupName ?? 'this group'}.
          </p>
        )}

        {rsvpEnabled && !isPast && !cancelled && !rsvpsForbidden && (
          <div>
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-flock-navy-400">
              Will you be there?
            </div>
            <div className="flex gap-2">
              {RSVP_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  disabled={rsvpMutation.isPending}
                  onClick={() => rsvpMutation.mutate(opt.value)}
                  className={clsx(
                    'min-h-touch flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition',
                    event.user_rsvp === opt.value
                      ? opt.activeClass
                      : 'border-flock-gray-200 text-flock-navy-600 hover:bg-flock-navy-50'
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {rsvpEnabled && rsvpData && (
          <div className="rounded-xl border border-flock-gray-200 p-3">
            <RsvpSummary counts={rsvpData.counts} />
            <div className="mt-3 border-t border-flock-gray-200 pt-3">
              <RsvpList
                eventId={event.id}
                organizationId={orgId}
                rsvps={rsvpData.rsvps}
                counts={rsvpData.counts}
                compact
                canNudge={!isPast && !cancelled && isLeader}
              />
            </div>
          </div>
        )}
      </div>

      <div className="shrink-0 border-t border-flock-gray-200 px-5 py-3">
        <Link
          to={`/events/${event.id}${isCrossOrg ? `?org=${orgId}` : ''}`}
          className="flex items-center justify-center gap-1.5 text-sm font-medium text-flock-navy-600 transition hover:text-flock-navy-800"
        >
          View full event & discussion <ArrowTopRightOnSquareIcon className="h-4 w-4" />
        </Link>
      </div>
    </>
  );
}

/**
 * Right-side slide-over panel. 'event' mode shows one event's details and
 * RSVP face wall; 'day' mode lists a day's events (from "+N more") and
 * drills into event mode with a back affordance.
 */
export default function EventSlideOver({ state, eventsByDay, onClose, onEdit, showGroup }: Props) {
  const [entered, setEntered] = useState(false);
  const [drillDown, setDrillDown] = useState<CalendarEventInstance | null>(null);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Reset drill-down when the slide-over target changes
  useEffect(() => {
    setDrillDown(null);
  }, [state]);

  const dayInstances =
    state.kind === 'day' ? (eventsByDay.get(dayKey(state.date)) ?? []) : [];

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
      <div
        onClick={onClose}
        className={clsx(
          'absolute inset-0 bg-black/30 transition-opacity duration-200',
          entered ? 'opacity-100' : 'opacity-0'
        )}
      />
      <div
        className={clsx(
          'absolute bottom-0 right-0 top-0 flex w-full max-w-md flex-col rounded-l-2xl bg-white shadow-2xl transition-transform duration-200',
          entered ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        <div className="flex items-center gap-1 px-3 pt-3">
          {state.kind === 'day' && drillDown && (
            <button
              onClick={() => setDrillDown(null)}
              aria-label="Back to day"
              className="rounded-lg p-2 text-flock-navy-400 transition hover:bg-flock-navy-50"
            >
              <ArrowLeftIcon className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={onClose}
            aria-label="Close"
            autoFocus
            className="ml-auto rounded-lg p-2 text-flock-navy-400 transition hover:bg-flock-navy-50"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {state.kind === 'event' ? (
          <EventBody
            eventId={state.eventId}
            organizationId={state.organizationId}
            occurrenceStart={state.occurrenceStart}
            isVirtual={state.isVirtual}
            groupId={state.groupId}
            groupName={state.groupName}
            onClose={onClose}
            onEdit={onEdit}
          />
        ) : drillDown ? (
          <EventBody
            eventId={drillDown.id}
            organizationId={drillDown.organizationId}
            occurrenceStart={drillDown.occurrenceStart}
            isVirtual={drillDown.isVirtual}
            groupId={drillDown.groupId}
            groupName={drillDown.groupName}
            onClose={onClose}
            onEdit={onEdit}
          />
        ) : (
          <div className="flex-1 overflow-y-auto px-5 pb-5">
            <h2 className="mb-3 text-lg font-semibold text-flock-navy-900">
              {format(state.date, 'EEEE, MMMM d')}
            </h2>
            <div className="space-y-2">
              {dayInstances.map((instance) => (
                <EventChip
                  key={instance.instanceKey}
                  instance={instance}
                  variant="row"
                  showGroup={showGroup}
                  onClick={() => setDrillDown(instance)}
                />
              ))}
              {dayInstances.length === 0 && (
                <p className="text-sm text-flock-navy-400">No events this day.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
